import eyeOffIcon from '@/assets/figma/login-eye-off.svg';
import infoWhiteIcon from '@/assets/figma/login-info-white.svg';
import { Button } from '@/shared/components/ui/button';
import { getApiErrorCode, isNetworkError } from '@/shared/api/http-client';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { cn } from '@/shared/lib/utils';
import { useAuthStore } from '@/stores/authStore';
import { LoginNoticeDialog } from '@/pages/login/components/LoginNoticeDialog';
import { rememberLastProvider } from '@/pages/login/components/SocialLoginButtons';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { Eye } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const loginSchema = z.object({
  email: z.string().email('올바른 형식의 이메일 주소를 입력해주세요.'),
  password: z.string().min(8, '비밀번호는 8자 이상 입력해주세요.'),
});
type LoginValues = z.infer<typeof loginSchema>;

/** M-02 실패 문구 — 없는 이메일 · 틀린 비밀번호 · 탈퇴 계정을 구분하지 않는다 */
const AUTH_FAILED = '이메일 또는 비밀번호가 올바르지 않아요.';
const BLOCKED = '시도 횟수를 초과했어요. 잠시 후 다시 시도해주세요.';
/** 네트워크 오류는 팝업으로 알린다 (Figma 848:9233) */
const NETWORK = {
  title: '연결이 불안정해요',
  body: '잠시 후 다시 시도해주세요.',
};

/** 회원가입 · 비밀번호 재설정은 앱 전용 — 누른 버튼 아래에 안내를 잠깐 띄운다 (Figma 864:10504) */
const APP_ONLY_LINKS = [
  { id: 'reset', label: '비밀번호 재설정' },
  { id: 'signup', label: '회원가입' },
] as const;
type AppOnlyLink = (typeof APP_ONLY_LINKS)[number]['id'];
const APP_ONLY_NOTICE_MS = 3000;

const fieldBox =
  'flex items-center gap-3 rounded-[20px] border-[1.5px] bg-white px-3 py-4 focus-within:border-[#131927]';
const fieldLabel =
  'text-[15px] leading-6 font-medium tracking-[-0.135px] text-[#394050]';
const fieldInput =
  'w-full bg-transparent text-base leading-6 tracking-[-0.16px] text-[#131927] outline-none placeholder:text-[#9ea2ae]';

export const LoginForm = ({
  disabled,
  redirectTo,
}: {
  disabled: boolean;
  /** 로그인 전에 들어오려던 내부 주소 — 성공하면 그리로 돌려보낸다 */
  redirectTo?: string;
}) => {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [networkError, setNetworkError] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [appOnly, setAppOnly] = useState<AppOnlyLink | null>(null);

  // 앱 전용 안내는 잠깐 보였다가 사라진다
  useEffect(() => {
    if (!appOnly) return;
    const timer = window.setTimeout(() => setAppOnly(null), APP_ONLY_NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [appOnly]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    mode: 'onBlur', // 형식 오류는 포커스 아웃 시점에 표시 (S3)
  });

  const [email, password] = watch(['email', 'password']);
  const filled = !!email && !!password;
  // 형식 오류 문구는 포커스 아웃 때 보여주지만, 버튼은 입력할 때마다 같은 스키마로 판정해
  // 형식이 맞지 않으면 비활성으로 둔다 (이메일 형식 · 비밀번호 8자 이상)
  const valid = loginSchema.safeParse({ email, password }).success;
  const hasAuthError = submitError === AUTH_FAILED;

  const onSubmit = async (values: LoginValues) => {
    setSubmitError(null);
    try {
      await login(values.email, values.password);
      rememberLastProvider('email');
      if (redirectTo) {
        void navigate({ href: redirectTo, replace: true });
      } else {
        void navigate({ to: ROUTE_PATHS.ACCOUNTS, replace: true });
      }
    } catch (error) {
      if (isNetworkError(error)) return setNetworkError(true);
      if (getApiErrorCode(error) === 'LOGIN_ATTEMPTS_EXCEEDED') {
        setBlocked(true);
        return setSubmitError(BLOCKED);
      }
      setSubmitError(AUTH_FAILED);
    }
  };

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-6"
        noValidate
      >
        <h1 className="text-[28px] leading-9 font-bold tracking-[-0.7px] text-[#131927]">
          로그인
        </h1>

        <fieldset disabled={disabled} className="flex flex-col gap-6">
          <label className="flex flex-col gap-1">
            <span className={fieldLabel}>이메일</span>
            <div
              className={cn(
                fieldBox,
                errors.email || hasAuthError
                  ? 'border-danger'
                  : 'border-[#e5e7ea]',
              )}
            >
              <input
                type="email"
                autoComplete="email"
                placeholder="name@company.com"
                className={fieldInput}
                {...register('email', {
                  // 차단은 이메일 기준이라 이메일을 바꾸면 차단 표시도 푼다
                  onChange: () => {
                    setBlocked(false);
                    setSubmitError(null);
                  },
                })}
              />
            </div>
            {errors.email && (
              <span className="mt-1 text-xs text-danger">
                {errors.email.message}
              </span>
            )}
          </label>

          <label className="flex flex-col gap-1">
            <span className={fieldLabel}>비밀번호</span>
            <div
              className={cn(
                fieldBox,
                errors.password || hasAuthError
                  ? 'border-danger'
                  : 'border-[#e5e7ea]',
              )}
            >
              <input
                type={showPassword ? 'text' : 'password'}
                // 비밀번호 자동완성은 브라우저 비밀번호 관리자에 맡긴다
                autoComplete="current-password"
                placeholder="비밀번호를 입력해주세요"
                className={fieldInput}
                {...register('password', {
                  // 차단 중에는 사유 문구를 남겨 둔다 (버튼이 왜 막혔는지 보이도록)
                  onChange: () => !blocked && setSubmitError(null),
                })}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="shrink-0"
                aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
              >
                {showPassword ? (
                  <Eye className="size-6 text-[#9ea2ae]" strokeWidth={1.5} />
                ) : (
                  <img src={eyeOffIcon} alt="" width={24} height={24} />
                )}
              </button>
            </div>
            {errors.password && (
              <span className="mt-1 text-xs text-danger">
                {errors.password.message}
              </span>
            )}
          </label>
        </fieldset>

        <div className="flex flex-col gap-3">
          {submitError && (
            <p className="text-xs text-danger" role="alert">
              {submitError}
            </p>
          )}
          <Button
            type="submit"
            loading={isSubmitting}
            disabled={!filled || !valid || blocked || disabled}
            className="h-14 rounded-[20px] bg-[#131927] text-lg leading-6 font-semibold tracking-[-0.27px] hover:bg-[#131927]/90 disabled:bg-[#d2d5db] disabled:text-white disabled:opacity-100"
          >
            로그인
          </Button>
        </div>

        {/* 안내는 데스크톱에서 누른 버튼 아래, 모바일에서는 화면을 넘지 않게 줄 가운데에 둔다 */}
        <div className="relative flex items-center justify-center px-6">
          {APP_ONLY_LINKS.map((link) => (
            <div
              key={link.id}
              className="flex flex-1 justify-center lg:relative"
            >
              <button
                type="button"
                onClick={() => setAppOnly(link.id)}
                className="flex h-10 w-full items-center justify-center rounded-xl px-2 text-sm leading-4 font-semibold tracking-[-0.112px] text-[#d2d5db]"
              >
                {link.label}
              </button>
              {appOnly === link.id && (
                <div
                  role="status"
                  className="absolute top-full left-1/2 z-20 flex -translate-x-1/2 gap-2 rounded-[20px] bg-[#131927] p-4"
                >
                  <img
                    src={infoWhiteIcon}
                    alt=""
                    width={24}
                    height={24}
                    className="shrink-0"
                  />
                  <p className="text-sm leading-[22px] font-medium tracking-[-0.112px] whitespace-nowrap text-white">
                    해당 기능은 현재 앱에서만 이용할 수 있어요.
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </form>

      <LoginNoticeDialog
        notice={networkError ? NETWORK : null}
        onClose={() => setNetworkError(false)}
      />
    </>
  );
};
